// src/dynamic-line-items-mapper/dynamic-line-items-builder.ts
import { defined as defined4, undefinedToNull as undefinedToNull3 } from "@vy/null";
import { isFixedPricing, isUsageLineItem, isUsagePricing } from "@vy/services";
import {
  COMMITMENT_TYPES,
  LINE_ITEM_TYPES as LINE_ITEM_TYPES2,
  PRICING_MODELS as PRICING_MODELS2
} from "@vy/types";
import {
  first as first2,
  isEmptyArray as isEmptyArray4,
  raise as raise3,
  raiseUnreachable as raiseUnreachable3,
  validateNotEmptyArray as validateNotEmptyArray2,
  validateNotNullOrUndefined as validateNotNullOrUndefined2
} from "@vy/utils";

// src/dynamic-line-items-mapper/dynamic-line-item-members/base-dynamic-line-item-member.ts
function getBaseUsageDynamicLineItemMember(variant) {
  return {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    variantId: variant.productVariantId,
    name: variant.name,
    meterInfo: buildMeterInfo(variant),
    dynamicLineItemPeriods: []
  };
}
function buildMeterInfo(variant) {
  return variant.meter ? {
    meterId: variant.meter.meterId,
    name: variant.meter.name,
    unitsType: variant.meter.unitsType ?? null,
    aggregationOperator: variant.meter.aggregationMethod.operator
  } : null;
}

// src/dynamic-line-items-mapper/dynamic-line-item-members/create-per-unit-dynamic-line-item-member.ts
import { undefinedToNull } from "@vy/null";
import { first, isEmptyArray, isNotEmptyArray, validateNotEmptyArray, vyDate } from "@vy/utils";

// src/dynamic-line-items-mapper/period-options-utils.ts
function shouldShowYtd(periodOptions) {
  return !periodOptions.hasMeterReset;
}
function shouldShowBillableQty(periodOptions) {
  return periodOptions.hasCommitment;
}

// src/dynamic-line-items-mapper/dynamic-line-item-members/has-meter-reset.ts
import { defined } from "@vy/null";
function hasMeterReset(lineItem) {
  return defined(lineItem.lineItemProduct.accumulationInterval) && lineItem.lineItemProduct.accumulationInterval > 0;
}

// src/dynamic-line-items-mapper/dynamic-line-item-members/create-per-unit-dynamic-line-item-member.ts
function getPerUnitDynamicLineItemMember(variant, usageData, pricing, lineItem) {
  const baseDynamicLineItemMember = getBaseUsageDynamicLineItemMember(variant);
  const dynamicLineItemPeriods = createPeriodsForPerUnitVariant(usageData, pricing, lineItem, {
    startTime: vyDate(lineItem.lineItemPeriod.startTime),
    endTime: vyDate(lineItem.lineItemPeriod.endTime)
  });
  return {
    ...baseDynamicLineItemMember,
    pricingConfiguration: pricing.priceConfiguration,
    dynamicLineItemPeriods
  };
}
function createPeriodsForPerUnitVariant(usageData, pricing, lineItem, fallbackFullPeriod) {
  const usagePeriodData = Object.entries(usageData.usagePeriods ?? {});
  const periodOptions = {
    hasCommitment: isNotEmptyArray(usageData.commitmentTierData),
    hasMeterReset: hasMeterReset(lineItem),
    isVolumePricing: false
  };
  if (isEmptyArray(usagePeriodData)) {
    return [
      {
        fromDate: fallbackFullPeriod.startTime.valueOf(),
        toDate: fallbackFullPeriod.endTime.valueOf(),
        entries: [
          {
            unitPrice: pricing.priceConfiguration.price,
            bulkSize: undefinedToNull(pricing.priceConfiguration.chunkSize),
            ytdQty: null,
            // TODO This is required here (if shouldShowYtd), but we've no way to get this right now
            billableQty: 0,
            periodQty: 0,
            amount: 0
          }
        ]
      }
    ];
  }
  const dynamicLineItemPeriods = usagePeriodData.map(
    ([calculationPeriodDate, breakdown]) => {
      const [startString, endString] = calculationPeriodDate.split("-");
      const startDate = vyDate(startString);
      const endDate = vyDate(endString);
      if (isEmptyArray(breakdown)) {
        return {
          fromDate: startDate.valueOf(),
          toDate: endDate.valueOf(),
          entries: [
            {
              unitPrice: pricing.priceConfiguration.price,
              bulkSize: undefinedToNull(pricing.priceConfiguration.chunkSize),
              ytdQty: null,
              // TODO This is required here (if shouldShowYtd), but we've no way to get this right now
              billableQty: shouldShowBillableQty(periodOptions) ? 0 : null,
              periodQty: 0,
              amount: 0
            }
          ]
        };
      }
      validateNotEmptyArray(
        breakdown,
        `No breakdown found for calculation period date: ${calculationPeriodDate}`
      );
      const dynamicLineItemPeriod = {
        fromDate: startDate.valueOf(),
        toDate: endDate.valueOf(),
        entries: createPerUnitEntriesForPeriod(
          first(breakdown),
          pricing.priceConfiguration,
          periodOptions,
          usageData.commitmentTierData[0]
        )
      };
      return dynamicLineItemPeriod;
    }
  );
  return dynamicLineItemPeriods.sort(
    (prevPeriod, currentPeriod) => vyDate(prevPeriod.fromDate).valueOf() - vyDate(currentPeriod.fromDate).valueOf()
  );
}
function createPerUnitEntriesForPeriod(usageData, pricing, periodOptions, commitmentUsage) {
  const dynamicFields = {
    billableQty: null,
    ytdQty: null
  };
  if (shouldShowYtd(periodOptions)) {
    dynamicFields.ytdQty = commitmentUsage?.ytdUnits ?? 0;
  }
  if (shouldShowBillableQty(periodOptions)) {
    dynamicFields.billableQty = usageData.measuredPrice.billableUnits;
  }
  return [
    {
      unitPrice: pricing.price,
      bulkSize: undefinedToNull(pricing.chunkSize),
      ytdQty: dynamicFields.ytdQty,
      billableQty: dynamicFields.billableQty,
      periodQty: usageData.measuredPrice.units,
      amount: usageData.measuredPrice.price
    }
  ];
}

// src/dynamic-line-items-mapper/dynamic-line-item-members/create-tiered-subscription-dynamic-line-item-member.ts
import { defined as defined2 } from "@vy/null";
import { isTieredSubscriptionPricing } from "@vy/services";
import {
  LINE_ITEM_TYPES
} from "@vy/types";
import {
  isEmptyArray as isEmptyArray2,
  isNotEmptyArray as isNotEmptyArray2,
  raise,
  raiseUnreachable,
  validateNotNullOrUndefined,
  vyDate as vyDate2
} from "@vy/utils";
function getTieredSubscriptionDynamicLineItemMember(variantId, variant, usageData, pricing, lineItem) {
  const baseDynamicLineItemMember = getBaseUsageDynamicLineItemMember(variant);
  const tieredSubscriptionDynamicLineItemMember = createTieredSubscriptionDynamicLineItemMember(
    baseDynamicLineItemMember,
    variant
  );
  const dynamicLineItemPeriods = createPeriodsForTieredSubscriptionVariant(
    usageData,
    variantId,
    pricing,
    lineItem
  );
  return {
    ...tieredSubscriptionDynamicLineItemMember,
    dynamicLineItemPeriods
  };
}
function createTieredSubscriptionEntriesForPeriod(breakdown, tiers, periodOptions, commitmentTierData = []) {
  const tiersList = [];
  for (let index = 0; index < tiers.length; index++) {
    const tier = tiers[index];
    const isLastTier = index === tiers.length - 1;
    const tierUsage = breakdown.find(
      (usage) => usage.tierIndex === index || usage.tierIndex == -1 && isLastTier
    );
    const commitmentUsage = commitmentTierData.find(
      (usage) => usage.tierIndex === index || usage.tierIndex == -1 && isLastTier
    );
    const dynamicFields = {
      billableQty: null,
      ytdQty: null
    };
    if (shouldShowYtd(periodOptions)) {
      dynamicFields.ytdQty = commitmentUsage?.ytdUnits ?? 0;
    }
    if (shouldShowBillableQty(periodOptions)) {
      dynamicFields.billableQty = tierUsage?.measuredPrice?.billableUnits ?? null;
    }
    tiersList.push({
      fromUnits: tier.from,
      toUnits: tier.to,
      flatRate: tier.flatRate ?? 0,
      ytdQty: dynamicFields.ytdQty,
      billableQty: dynamicFields.billableQty,
      periodQty: tierUsage?.measuredPrice?.units ?? 0,
      amount: tierUsage?.measuredPrice?.price ?? 0
    });
  }
  return tiersList;
}
function createTieredSubscriptionDynamicLineItemMember(baseMember, variant) {
  const pricing = variant.pricing;
  if (!isTieredSubscriptionPricing(pricing)) {
    raise(`Pricing must be tiered subscription pricing for tiered subscription dynamic line item member`);
  }
  return {
    ...baseMember,
    pricingConfiguration: pricing.priceConfiguration,
    useInfinity: pricing.priceConfiguration.useInfinity ?? false
  };
}
function createPeriodsForTieredSubscriptionVariant(usageData, targetVariantId, pricing, lineItem) {
  const periods = [];
  const lineItemType = lineItem.type;
  const periodOptions = {
    hasCommitment: isNotEmptyArray2(usageData.commitmentTierData),
    hasMeterReset: hasMeterReset(lineItem),
    isVolumePricing: pricing.priceConfiguration.isVolume
  };
  switch (lineItemType) {
    case LINE_ITEM_TYPES.Fixed: {
      const prepaymentPeriod = createPrepaymentPeriodForTieredSubscriptionVariant(
        targetVariantId,
        pricing,
        lineItem,
        periodOptions
      );
      periods.push(prepaymentPeriod);
      break;
    }
    case LINE_ITEM_TYPES.Usage: {
      const postPaymentPeriods = createPostpaymentPeriodsForTieredSubscriptionVariant(
        lineItem,
        usageData,
        pricing,
        periodOptions
      );
      if (defined2(postPaymentPeriods)) {
        periods.push(...postPaymentPeriods);
      }
      break;
    }
    case LINE_ITEM_TYPES.NonProductFixed:
      return [];
    case LINE_ITEM_TYPES.Commitment: {
      const commitmentPeriod = createCommitmentPeriodForTieredSubscriptionVariant(
        pricing,
        lineItem,
        periodOptions
      );
      periods.push(commitmentPeriod);
      break;
    }
    default:
      return raiseUnreachable(lineItemType);
  }
  return periods.sort(
    (prevPeriod, currentPeriod) => vyDate2(prevPeriod.fromDate).valueOf() - vyDate2(currentPeriod.fromDate).valueOf()
  );
}
function createCommitmentPeriodForTieredSubscriptionVariant(pricing, lineItem, periodOptions) {
  return {
    fromDate: vyDate2(0).valueOf(),
    toDate: vyDate2(0).valueOf(),
    entries: createTieredSubscriptionEntriesForPeriod(
      [
        {
          tierIndex: 0,
          measuredPrice: {
            units: 0,
            billableUnits: 0,
            price: lineItem.revenueBreakdown.total,
            currency: "USD",
            nonBillableUnits: 0
          }
        }
      ],
      pricing.priceConfiguration.tiers,
      periodOptions
    )
  };
}
function createPrepaymentPeriodForTieredSubscriptionVariant(targetVariantId, pricing, lineItem, periodOptions) {
  const invoiceCharge = lineItem.invoiceCharge;
  if (!defined2(invoiceCharge)) {
    return {
      fromDate: vyDate2(lineItem.lineItemPeriod.startTime).valueOf(),
      toDate: vyDate2(lineItem.lineItemPeriod.endTime).valueOf(),
      entries: createTieredSubscriptionEntriesForPeriod([], pricing.priceConfiguration.tiers, periodOptions)
    };
  }
  const commitmentTier = invoiceCharge.calculationBreakdown.variantTierBreakdown.find(
    (breakdown2) => breakdown2.variantId === targetVariantId
  );
  validateNotNullOrUndefined(commitmentTier, `Commitment tier not found for variantId: ${targetVariantId}`);
  const { currency } = invoiceCharge.calculationBreakdown;
  const breakdown = commitmentTier.commitmentTiers.map((commitmentTierInfo) => ({
    tierIndex: commitmentTierInfo.chargeInfo.tierContext.tierIndex,
    measuredPrice: {
      units: commitmentTierInfo.chargeInfo.periodUnits,
      billableUnits: commitmentTierInfo.commitmentContext.billableUnits,
      nonBillableUnits: commitmentTierInfo.chargeInfo.periodUnits - commitmentTierInfo.commitmentContext.billableUnits,
      price: commitmentTierInfo.chargeInfo.price,
      currency
    }
  }));
  return {
    fromDate: vyDate2(lineItem.lineItemPeriod.startTime).valueOf(),
    toDate: vyDate2(lineItem.lineItemPeriod.endTime).valueOf(),
    entries: createTieredSubscriptionEntriesForPeriod(
      breakdown,
      pricing.priceConfiguration.tiers,
      periodOptions
    )
  };
}
function createPostpaymentPeriodsForTieredSubscriptionVariant(lineItem, usageData, pricing, periodOptions) {
  const usagePeriodData = Object.entries(usageData.usagePeriods ?? {});
  if (isEmptyArray2(usagePeriodData)) {
    const fallbackFullPeriod = {
      startTime: vyDate2(lineItem.lineItemPeriod.startTime),
      endTime: vyDate2(lineItem.lineItemPeriod.endTime)
    };
    const emptyPeriod = {
      fromDate: fallbackFullPeriod.startTime.valueOf(),
      toDate: fallbackFullPeriod.endTime.valueOf(),
      entries: createTieredSubscriptionEntriesForPeriod(
        [],
        pricing.priceConfiguration.tiers,
        periodOptions,
        usageData.commitmentTierData
      )
    };
    return [emptyPeriod];
  }
  const postpaymentPeriods = usagePeriodData.map(([calculationPeriodDate, breakdown]) => {
    const [startString, endString] = calculationPeriodDate.split("-");
    const startDate = vyDate2(startString);
    const endDate = vyDate2(endString);
    const postpaymentPeriod = {
      fromDate: startDate.valueOf(),
      toDate: endDate.valueOf(),
      entries: createTieredSubscriptionEntriesForPeriod(
        breakdown,
        pricing.priceConfiguration.tiers,
        periodOptions,
        usageData.commitmentTierData
      )
    };
    return postpaymentPeriod;
  });
  return postpaymentPeriods;
}

// src/dynamic-line-items-mapper/dynamic-line-item-members/create-tiers-dynamic-line-item-member.ts
import { defined as defined3, undefinedToNull as undefinedToNull2 } from "@vy/null";
import {
  PRICING_MODELS
} from "@vy/types";
import { isEmptyArray as isEmptyArray3, isNotEmptyArray as isNotEmptyArray3, raise as raise2, vyDate as vyDate3 } from "@vy/utils";
function getTiersDynamicLineItemMember(variant, usageData, pricing, lineItem) {
  const baseDynamicLineItemMember = getBaseUsageDynamicLineItemMember(variant);
  const tieredDynamicLineItemMember = createTieredDynamicLineItemMember(baseDynamicLineItemMember, variant);
  const dynamicLineItemPeriods = createPeriodsForTiersVariant(usageData, pricing, lineItem, {
    startTime: vyDate3(lineItem.lineItemPeriod.startTime),
    endTime: vyDate3(lineItem.lineItemPeriod.endTime)
  });
  return {
    ...tieredDynamicLineItemMember,
    dynamicLineItemPeriods
  };
}
function createTiersEntriesForPeriod(breakdown, tiers, periodOptions, commitmentData = []) {
  const tiersList = [];
  for (let index = 0; index < tiers.length; index++) {
    const tier = tiers[index];
    const usageValues = {
      billableUnits: null,
      periodQty: 0,
      price: 0
    };
    if (defined3(breakdown)) {
      const tierUsage = breakdown.find((usage) => usage.tierIndex === index);
      if (defined3(tierUsage)) {
        if (shouldShowBillableQty(periodOptions)) {
          usageValues.billableUnits = tierUsage.measuredPrice.billableUnits;
        }
        usageValues.periodQty = tierUsage.measuredPrice.units;
        usageValues.price = tierUsage.measuredPrice.price;
      }
    }
    const commitmentUsage = commitmentData.find((usage) => usage.tierIndex === index);
    let ytdUnits = null;
    if (shouldShowYtd(periodOptions)) {
      ytdUnits = commitmentUsage?.ytdUnits ?? 0;
    }
    tiersList.push({
      fromUnits: tier.from,
      toUnits: tier.to,
      price: tier.price,
      flatRate: undefinedToNull2(tier.flatRate),
      bulkSize: undefinedToNull2(tier.chunkSize),
      ytdQty: ytdUnits,
      billableQty: usageValues.billableUnits,
      periodQty: usageValues.periodQty,
      amount: usageValues.price
    });
  }
  return tiersList;
}
function createTieredDynamicLineItemMember(baseMember, variant) {
  const pricing = variant.pricing;
  if (pricing.pricingModel !== PRICING_MODELS.Tiers) {
    raise2(`Invalid pricing model for tiered dynamic line item member: ${pricing.pricingModel}`);
  }
  return {
    ...baseMember,
    pricingConfiguration: pricing.priceConfiguration,
    isRevShare: pricing.priceConfiguration.isRevShare,
    useInfinity: pricing.priceConfiguration.useInfinity
  };
}
function createPeriodsForTiersVariant(usageData, pricing, lineItem, fallbackFullPeriod) {
  const dynamicLineItemPeriods = [];
  const usagePeriodData = Object.entries(usageData.usagePeriods ?? {});
  const periodOptions = {
    hasCommitment: isNotEmptyArray3(usageData.commitmentTierData),
    hasMeterReset: hasMeterReset(lineItem),
    isVolumePricing: pricing.priceConfiguration.isVolume
  };
  if (isEmptyArray3(usagePeriodData)) {
    return [
      {
        fromDate: fallbackFullPeriod.startTime.valueOf(),
        toDate: fallbackFullPeriod.endTime.valueOf(),
        entries: createTiersEntriesForPeriod(null, pricing.priceConfiguration.tiers, periodOptions)
      }
    ];
  }
  usagePeriodData.forEach(([calculationPeriodDate, breakdown]) => {
    const [startString, endString] = calculationPeriodDate.split("-");
    const startDate = vyDate3(startString);
    const endDate = vyDate3(endString);
    const dynamicLineItemPeriod = {
      fromDate: startDate.valueOf(),
      toDate: endDate.valueOf(),
      entries: createTiersEntriesForPeriod(breakdown, pricing.priceConfiguration.tiers, periodOptions)
    };
    dynamicLineItemPeriods.push(dynamicLineItemPeriod);
  });
  return dynamicLineItemPeriods.sort(
    (prevPeriod, currentPeriod) => vyDate3(prevPeriod.fromDate).valueOf() - vyDate3(currentPeriod.fromDate).valueOf()
  );
}

// src/dynamic-line-items-mapper/usage-data-reader.ts
import { raiseUnreachable as raiseUnreachable2 } from "@vy/utils";
function readUsageDataForVariant(lineItem, variantId) {
  const lineItemType = lineItem.type;
  switch (lineItemType) {
    case "Commitment":
      return lineItem.commitmentTiersUsageMap[variantId] || [];
    case "Usage":
      return lineItem.tiersUsageMap[variantId] || [];
    case "Fixed":
    case "NonProductFixed":
      return [];
    default:
      return raiseUnreachable2(lineItemType);
  }
}

// src/dynamic-line-items-mapper/dynamic-line-items-builder.ts
function buildDynamicLineItems(measuredPriceBreakdownMap, lineItems) {
  if (isEmptyArray4(lineItems)) {
    raise3("No line items found on the invoice");
  }
  const dynamicLineItems = lineItems.map(
    (lineItem) => createDynamicLineItem(lineItem, measuredPriceBreakdownMap)
  );
  validateNotEmptyArray2(dynamicLineItems, "No dynamic line items were built from the invoice line items");
  return dynamicLineItems;
}
function createDynamicLineItem(lineItem, measuredPriceBreakdownMap) {
  const lineItemVariants = lineItem.lineItemProduct.variants;
  validateNotEmptyArray2(lineItemVariants, `No variants found for line item ${lineItem.id}`);
  if (!lineItem.lineItemPeriod) {
    lineItem.lineItemPeriod = {
      startTime: lineItem.createdAt,
      endTime: lineItem.createdAt
    };
  }
  validateNotNullOrUndefined2(
    lineItem.lineItemPeriod,
    `lineItemPeriod is null or undefined for line item ${lineItem.id}`
  );
  const firstVariant = first2(lineItemVariants);
  if (isFixedPricing(firstVariant.pricing)) {
    return buildFixedLineItem(lineItem, firstVariant, firstVariant.pricing);
  }
  const isManualLineItem = lineItem.type === LINE_ITEM_TYPES2.NonProductFixed;
  if (isManualLineItem) {
    raise3(`Line item ${lineItem.id} is manual but has usage pricing`);
  }
  return buildUsageLineItem(lineItem, measuredPriceBreakdownMap, lineItemVariants);
}
function getBaseLineItemMember(lineItem, firstVariant, productId) {
  return {
    lineItemId: lineItem.id,
    productId: undefinedToNull3(productId),
    name: lineItem.modifiedName ?? lineItem.lineItemProduct.name ?? firstVariant.name,
    description: lineItem.modifiedDescription,
    basePrice: lineItem.lineItemProduct.basePrice ?? null,
    totalAmount: lineItem.revenueBreakdown.total,
    disabled: lineItem.disabled ?? false,
    isRevised: lineItem.isRevised,
    originalInvoiceData: undefinedToNull3(lineItem.originalInvoiceData)
  };
}
function buildFixedLineItem(lineItem, firstVariant, pricing) {
  return {
    ...getBaseLineItemMember(lineItem, firstVariant, firstVariant.productVariantId),
    ...pricing,
    variantId: undefinedToNull3(firstVariant.productVariantId)
  };
}
function buildUsageLineItem(lineItem, measuredPriceBreakdownMap, lineItemVariants) {
  const isPrepaymentLineItem = lineItem.type === LINE_ITEM_TYPES2.Fixed;
  const prepaymentPeriodUnits = lineItem.invoiceCharge.calculationBreakdown.periodUnits;
  if (!isUsageLineItem(lineItem) && lineItem.type !== LINE_ITEM_TYPES2.Commitment) {
    raise3(`Line item ${lineItem.id} is neither usage nor commitment line item`);
  }
  const firstVariant = first2(lineItemVariants);
  if (!isUsagePricing(firstVariant.pricing)) {
    raise3(`Variant ${firstVariant.productVariantId} of line item ${lineItem.id} does not have usage pricing`);
  }
  const dynamicLineItemMembers = [];
  const measuredPriceBreakdownObjects = Object.entries(measuredPriceBreakdownMap);
  for (const variant of lineItemVariants) {
    if (!isUsagePricing(variant.pricing)) {
      raise3(`Variant ${variant.productVariantId} of line item ${lineItem.id} does not have usage pricing`);
    }
    const variantId = variant.productVariantId;
    const variantUsage = measuredPriceBreakdownObjects.find(([breakdownVariantId, _periods]) => {
      return breakdownVariantId === variantId;
    })?.[1];
    const usageData = {
      usagePeriods: variantUsage,
      commitmentTierData: readUsageDataForVariant(lineItem, variantId)
    };
    const member = createDynamicLineItemMember(
      variant,
      usageData,
      variant.pricing,
      lineItem
    );
    if (isEmptyArray4(member.dynamicLineItemPeriods)) {
      raise3(`No usage data found for line item ${lineItem.id} variant ${variant.productVariantId}`);
    }
    dynamicLineItemMembers.push(member);
  }
  validateNotEmptyArray2(dynamicLineItemMembers, `No variants found for usage line item ${lineItem.id}`);
  return {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    ...getBaseLineItemMember(lineItem, firstVariant, firstVariant.productVariantId),
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    productId: lineItem.lineItemProduct.productId,
    pricingModel: firstVariant.pricing.pricingModel,
    commitment: getOrBuildCommitment(lineItem),
    totalUnits: isPrepaymentLineItem ? prepaymentPeriodUnits : lineItem.units,
    dynamicLineItemMembers
  };
}
function getOrBuildCommitment(lineItem) {
  if (!lineItem.lineItemProduct.commitment) {
    return null;
  }
  if (lineItem.lineItemProduct.commitment.type !== COMMITMENT_TYPES.Price) {
    return lineItem.lineItemProduct.commitment;
  }
  const consumption = lineItem.invoiceCharge?.calculationBreakdown?.ytdCommitmentUsed;
  if (!defined4(consumption)) {
    return null;
  }
  return {
    consumption,
    amount: lineItem.lineItemProduct.commitment.amount,
    type: COMMITMENT_TYPES.Price,
    replenishmentInterval: lineItem.lineItemProduct.commitment.replenishmentInterval
  };
}
function createDynamicLineItemMember(variant, usageData, pricing, lineItem) {
  const variantId = variant.productVariantId;
  switch (pricing.pricingModel) {
    case PRICING_MODELS2.Tiers: {
      return getTiersDynamicLineItemMember(variant, usageData, pricing, lineItem);
    }
    case PRICING_MODELS2.TieredSubscription: {
      return getTieredSubscriptionDynamicLineItemMember(variantId, variant, usageData, pricing, lineItem);
    }
    case PRICING_MODELS2.PerUnit: {
      return getPerUnitDynamicLineItemMember(variant, usageData, pricing, lineItem);
    }
    default:
      raiseUnreachable3(pricing);
      break;
  }
  raiseUnreachable3(pricing);
  return null;
}

// src/dynamic-line-item-types/dynamic-line-item-utils/is-fixed-dynamic-line-item.ts
import { isFixedPricingModel } from "@vy/types";
function isFixedDynamicLineItem(lineItem) {
  return isFixedPricingModel(lineItem.pricingModel);
}

// src/dynamic-line-item-types/dynamic-line-item-utils/is-usage-dynamic-line-item.ts
import { isUsagePricingModel } from "@vy/types";
function isUsageDynamicLineItem(lineItem) {
  return isUsagePricingModel(lineItem.pricingModel);
}
export {
  buildDynamicLineItems,
  isFixedDynamicLineItem,
  isUsageDynamicLineItem
};
